import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { Alert, Button, Card, Dropdown, Flex, Grid, Image, Modal, Space, Tag, Typography } from "antd";
import type { MenuProps } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faGripVertical,
  faImage,
  faMagnifyingGlassPlus,
  faPaperclip,
  faPlus,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import { AllegatiPanel } from "../Form/ExerciseProgressionSetRow";
import VideoThumb from "../../common/VideoThumb";
import { isVideoSrc } from "../../utils/mediaKind";
import type { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { ExerciseEntry, ExerciseProgression } from "../../types/Programmazione";
import type { PrecompiledSet, PrecompiledWeek } from "../../types/Entities";
import { useClipboard } from "../../contexts/clipboard";
import { useUploadIndicator } from "../../contexts/uploadIndicator";
import { usePrecompiledProgressionName } from "../../hooks/usePrecompiledProgressionName";
import { usePrecompiledProgressionMeta } from "../../hooks/usePrecompiledProgressionMeta";
import {
  applyLibraryUpdate,
  hasLibraryUpdate,
  mergeAttachedProgression,
  shouldPromptAttachMode,
} from "../../utils/progressionLink";
import { EntityOptionsState, type ProgEditorOptions } from "../../hooks/useEntityOptions";
import { useProgrammazioneMutations } from "../../hooks/useProgrammazioneMutations";
import type { Option } from "../../types/generics";
import ProgrammazioneDetailEditor from "./ProgrammazioneDetailEditor";
import type { PrecompiledProgressionPickerResult } from "../Form/PrecompiledProgressionPicker";
import ActionButtons from "./ActionButtons";
import ExerciseIdentityModal, { type ExerciseIdentity } from "./ExerciseIdentityModal";
import { faPen, faCalendarPlus, faFileImport, faLinkSlash, faLink, faRotate } from "@fortawesome/free-solid-svg-icons";

// Lazy-loaded (modal-only) so their module graphs (incl. the axios API client
// in the picker) stay out of this file's static graph — keeps pure-util
// imports of this module transform-free in tests.
const ExerciseProgressionEditor = lazy(() => import("../Form/ExerciseProgressionEditor"));
const PrecompiledProgressionPicker = lazy(() => import("../Form/PrecompiledProgressionPicker"));

// PROG-DETAIL/IMPL K2 — extracted from the inline JSX so it doesn't
// re-evaluate as a literal on every render of every exercise card.
const IMAGE_FALLBACK =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjOTk5IiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+";

// PROG-SET-MEDIA/FE-VIEW-1 (#0c44c27b) — per-set Allegati on the Detail page.
//
// Architect plan #9d45d675 §4 + user decision Q5.
//
// Walks `exercise.progression?.weeks[*].sets[*]` and surfaces ONLY those sets
// that carry a `note`, `imageUrl`, or `videoUrl`. Each surfaced set renders:
//   - a header label "Sett. <weekNumber> · Set <setIndex+1>",
//   - an italic note paragraph beneath when `note` is non-empty,
//   - a "Media allegato" chip (geekblue for image, purple for video) that
//     opens a Modal preview (Q5 — no inline thumbnail).
//
// Read-only; the editor lives in FE-EDITOR-1. Empty exercises (no
// progression, or all sets clean) render NOTHING — the panel is opt-in.

export type SetMediaItem = {
  weekNumber: number;
  setIndex: number;
  note?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
};

type SetPreview =
  | { kind: "image"; src: string; label: string }
  | { kind: "video"; src: string; label: string };

export function collectSetMedia(
  weeks: PrecompiledWeek[] | undefined,
): SetMediaItem[] {
  const out: SetMediaItem[] = [];
  if (!Array.isArray(weeks)) return out;
  weeks.forEach((week) => {
    const sets = (week.sets ?? []) as PrecompiledSet[];
    sets.forEach((set, setIndex) => {
      const hasNote = !!set.note;
      const hasImage = !!set.imageUrl;
      const hasVideo = !!set.videoUrl;
      if (!hasNote && !hasImage && !hasVideo) return;
      out.push({
        weekNumber: week.weekNumber ?? 0,
        setIndex,
        note: set.note ?? null,
        imageUrl: set.imageUrl ?? null,
        videoUrl: set.videoUrl ?? null,
      });
    });
  });
  return out;
}

// PROG-SET-MEDIA/FE-VIEW-2 — internal enumeration that, unlike the exported
// `collectSetMedia` (read-only, surfaces only sets WITH media), walks every
// set and carries its array `weekIndex` so the panel can target
// `mutations.updateProgressionSet(... weekIndex, setIndex ...)`. `weekNumber`
// is kept only for the human-facing "Sett. N" label.
type EditableSetMedia = {
  weekIndex: number;
  weekNumber: number;
  setIndex: number;
  set: PrecompiledSet;
};

function collectEditableSetMedia(
  weeks: PrecompiledWeek[] | undefined,
): EditableSetMedia[] {
  const out: EditableSetMedia[] = [];
  if (!Array.isArray(weeks)) return out;
  weeks.forEach((week, weekIndex) => {
    const sets = (week.sets ?? []) as PrecompiledSet[];
    sets.forEach((set, setIndex) => {
      out.push({
        weekIndex,
        weekNumber: week.weekNumber ?? 0,
        setIndex,
        set,
      });
    });
  });
  return out;
}

const hasSetMediaContent = (set: PrecompiledSet): boolean =>
  !!set.note ||
  !!set.imageUrl ||
  !!set.videoUrl ||
  (set.trainerMedia?.length ?? 0) > 0 ||
  (set.athleteMedia?.length ?? 0) > 0;

function SetMediaSummary({
  exercise,
  splitIndex,
  exIndex,
  canEdit,
  isTargetAthlete,
  mutations,
}: {
  exercise: ExerciseEntry;
  splitIndex: number;
  exIndex: number;
  canEdit: boolean;
  isTargetAthlete: boolean;
  mutations?: ReturnType<typeof useProgrammazioneMutations>;
}) {
  const allItems = useMemo(
    () =>
      collectEditableSetMedia(
        (exercise.progression as { weeks?: PrecompiledWeek[] } | undefined)
          ?.weeks,
      ),
    [exercise.progression],
  );
  // PROG-SET-MEDIA/FE-VIEW-2 — both the trainer (canEdit) and the target
  // athlete may attach per-set media here, mirroring ATHLETE_FIELDS in the
  // Detail editor (which already lists "media"). Editing needs the mutations
  // bundle to commit.
  const canManage = (canEdit || isTargetAthlete) && !!mutations;

  const displayed = useMemo(
    () => allItems.filter((it) => hasSetMediaContent(it.set)),
    [allItems],
  );

  const { track } = useUploadIndicator();
  const [preview, setPreview] = useState<SetPreview | null>(null);
  // Bumped on every open so the <video> below is keyed to a fresh element each
  // time — otherwise a reused media element only replays on alternate opens.
  const [previewNonce, setPreviewNonce] = useState(0);
  const openPreview = useCallback((p: SetPreview) => {
    setPreviewNonce((n) => n + 1);
    setPreview(p);
  }, []);
  // Staged shallow copy of the focused set — shallow (not JSON-cloned) so the
  // transient `image` / `video` File handles survive (see DETAIL-NEW/6).
  const [mediaEdit, setMediaEdit] = useState<{
    weekIndex: number;
    setIndex: number;
    weekNumber: number;
    stagedSet: PrecompiledSet;
  } | null>(null);

  const openMediaEdit = useCallback(
    (item: EditableSetMedia) =>
      setMediaEdit({
        weekIndex: item.weekIndex,
        setIndex: item.setIndex,
        weekNumber: item.weekNumber,
        stagedSet: { ...item.set },
      }),
    [],
  );

  // Mirrors ProgrammazioneDetailEditor.commitMedia — hands the note + image /
  // video (File) + their *Url counterparts to the page-level mutation, which
  // strips File handles into FormData under the canonical bracket-path keys.
  const commitMedia = useCallback(() => {
    if (!mutations || !mediaEdit) return;
    const s = mediaEdit.stagedSet;
    const successMsg =
      s.video || s.videoUrl
        ? "Video salvato correttamente"
        : s.image || s.imageUrl
          ? "Immagine salvata correttamente"
          : "Allegati del set salvati";
    const galleryHasNewFile = [
      ...(s.trainerMedia ?? []),
      ...(s.athleteMedia ?? []),
    ].some((m) => m.file instanceof File);
    const isUpload =
      s.image instanceof File || s.video instanceof File || galleryHasNewFile;
    // Fire the save synchronously (the patch — including the File — is cloned
    // into the mutation before the first await), then close the modal
    // immediately so the upload runs in the background, surfaced by the global
    // indicator (spinner → green check). The modal must NOT stay open/blocked
    // while a (possibly slow) upload is in flight.
    const saving = Promise.resolve(
      mutations.updateProgressionSet(
        splitIndex,
        exIndex,
        mediaEdit.weekIndex,
        mediaEdit.setIndex,
        {
          note: s.note ?? null,
          image: s.image,
          imageUrl: s.imageUrl ?? null,
          video: s.video,
          videoUrl: s.videoUrl ?? null,
          // Multi-media galleries (the BE restores the caller's non-owned role
          // from the DB, so sending both is safe).
          trainerMedia: s.trainerMedia ?? null,
          athleteMedia: s.athleteMedia ?? null,
        },
        successMsg,
      ),
    );
    if (isUpload) track(saving);
    setMediaEdit(null);
  }, [mutations, mediaEdit, splitIndex, exIndex, track]);

  // "Aggiungi media a un set" — pick any set (incl. ones with no media yet) and
  // open the same per-set image/video editor.
  const addMenuItems: MenuProps["items"] = useMemo(
    () =>
      allItems.map((it) => ({
        key: `${it.weekIndex}:${it.setIndex}`,
        label: `Sett. ${it.weekNumber} · Set ${it.setIndex + 1}${
          hasSetMediaContent(it.set) ? " · media presente" : ""
        }`,
      })),
    [allItems],
  );
  const onAddMenuClick = useCallback<NonNullable<MenuProps["onClick"]>>(
    ({ key }) => {
      const [wi, si] = String(key).split(":").map(Number);
      const item = allItems.find(
        (it) => it.weekIndex === wi && it.setIndex === si,
      );
      if (item) openMediaEdit(item);
    },
    [allItems, openMediaEdit],
  );

  // Opt-in for read-only viewers (only when media exists); for managers the
  // panel also appears whenever there is at least one set to attach media to.
  if (displayed.length === 0 && !(canManage && allItems.length > 0)) return null;

  return (
    <div
      style={{
        marginTop: 4,
        padding: 12,
        border: "1px dashed var(--app-gray-300)",
        borderRadius: 8,
        background: "var(--app-gray-50)",
      }}
      data-testid="set-media-summary"
    >
      <Typography.Text strong style={{ display: "block", marginBottom: 8 }}>
        <FontAwesomeIcon icon={faPaperclip} className="me-2" />
        Allegati ai set
      </Typography.Text>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        {displayed.map((item) => {
          const { set } = item;
          const label = `Sett. ${item.weekNumber} · Set ${item.setIndex + 1}`;
          const key = `${item.weekIndex}-${item.setIndex}`;
          return (
            <div
              key={key}
              data-testid={`set-media-item-${item.weekNumber}-${item.setIndex}`}
            >
              <Flex align="center" gap={4} wrap="wrap">
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {label}
                </Typography.Text>
                {canManage && (
                  <Button
                    type="text"
                    size="small"
                    icon={<FontAwesomeIcon icon={faPen} />}
                    onClick={() => openMediaEdit(item)}
                    title="Modifica media del set"
                    aria-label={`Modifica media ${label}`}
                    data-testid={`set-media-edit-${item.weekNumber}-${item.setIndex}`}
                  />
                )}
              </Flex>
              {set.note && (
                <Typography.Paragraph
                  italic
                  style={{ margin: "4px 0 4px 0", whiteSpace: "pre-wrap" }}
                  data-testid={`set-media-note-${item.weekNumber}-${item.setIndex}`}
                >
                  {set.note}
                </Typography.Paragraph>
              )}
              {(set.imageUrl || set.videoUrl) && (
                <Space wrap>
                  {set.imageUrl && (
                    <Tag
                      color="geekblue"
                      icon={<FontAwesomeIcon icon={faImage} />}
                      style={{ cursor: "pointer", borderRadius: 12 }}
                      onClick={() =>
                        openPreview({
                          kind: "image",
                          src: set.imageUrl!,
                          label,
                        })
                      }
                      data-testid={`set-media-image-chip-${item.weekNumber}-${item.setIndex}`}
                    >
                      Immagine
                    </Tag>
                  )}
                  {set.videoUrl && (
                    <Tag
                      color="purple"
                      icon={<FontAwesomeIcon icon={faVideo} />}
                      style={{ cursor: "pointer", borderRadius: 12 }}
                      onClick={() =>
                        openPreview({
                          kind: "video",
                          src: set.videoUrl!,
                          label,
                        })
                      }
                      data-testid={`set-media-video-chip-${item.weekNumber}-${item.setIndex}`}
                    >
                      Video
                    </Tag>
                  )}
                </Space>
              )}
              {/* Multi-media galleries (trainer + athlete) — each item is a
                  clickable thumbnail/chip that opens the preview. */}
              {(() => {
                const gallery = [
                  ...(set.trainerMedia ?? []),
                  ...(set.athleteMedia ?? []),
                ].filter((m) => m.signedUrl);
                if (gallery.length === 0) return null;
                return (
                  <Space wrap style={{ marginTop: 6 }}>
                    {gallery.map((m, gi) => {
                      const src = m.signedUrl!;
                      if (m.type === "video") {
                        return (
                          <VideoThumb
                            key={gi}
                            src={src}
                            title={m.name || "Video"}
                            onClick={() =>
                              openPreview({ kind: "video", src, label })
                            }
                            style={{
                              width: 44,
                              height: 44,
                              border: "1px solid var(--app-gray-200)",
                            }}
                          />
                        );
                      }
                      if (m.type === "document") {
                        return (
                          <Tag
                            key={gi}
                            color="gold"
                            icon={<FontAwesomeIcon icon={faPaperclip} />}
                            className="set-media-chip"
                            style={{ cursor: "pointer" }}
                            title={m.name || "Documento"}
                            onClick={() =>
                              window.open(src, "_blank", "noopener,noreferrer")
                            }
                          >
                            <span className="set-media-chip__label">
                              {m.name || "Documento"}
                            </span>
                          </Tag>
                        );
                      }
                      return (
                        <img
                          key={gi}
                          src={src}
                          alt={m.name || "Media"}
                          style={{
                            width: 44,
                            height: 44,
                            objectFit: "cover",
                            borderRadius: 8,
                            border: "1px solid var(--app-gray-200)",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            openPreview({ kind: "image", src, label })
                          }
                        />
                      );
                    })}
                  </Space>
                );
              })()}
            </div>
          );
        })}
        {displayed.length === 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Nessun media allegato ai set.
          </Typography.Text>
        )}
      </Space>

      {canManage && allItems.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Dropdown
            trigger={["click"]}
            menu={{ items: addMenuItems, onClick: onAddMenuClick }}
          >
            <Button
              size="small"
              icon={<FontAwesomeIcon icon={faPlus} />}
              data-testid="set-media-add"
            >
              Aggiungi / modifica media
            </Button>
          </Dropdown>
        </div>
      )}

      <Modal
        open={!!preview}
        onCancel={() => setPreview(null)}
        title={preview?.label}
        footer={null}
        destroyOnClose
        width={preview?.kind === "video" ? 720 : 520}
        data-testid="set-media-preview-modal"
      >
        {preview?.kind === "image" && (
          <Image
            key={previewNonce}
            src={preview.src}
            alt={preview.label}
            style={{ width: "100%" }}
            preview={false}
          />
        )}
        {preview?.kind === "video" && (
          <video
            key={previewNonce}
            src={preview.src}
            controls
            autoPlay
            style={{ width: "100%", maxHeight: "70vh", background: "#000" }}
          />
        )}
      </Modal>

      {mediaEdit && (
        <Modal
          open
          title={`Allegati — Sett. ${mediaEdit.weekNumber} · Set ${
            mediaEdit.setIndex + 1
          }`}
          okText="Salva"
          cancelText="Annulla"
          onOk={commitMedia}
          onCancel={() => setMediaEdit(null)}
          destroyOnClose
          data-testid={`set-media-edit-modal-${mediaEdit.weekIndex}-${mediaEdit.setIndex}`}
        >
          <AllegatiPanel
            wi={mediaEdit.weekIndex}
            si={mediaEdit.setIndex}
            set={mediaEdit.stagedSet}
            mediaLocked={false}
            chromeless
            showPrimaryMedia={false}
            // Multiple media per set: the trainer fills the "Media trainer"
            // gallery, the target athlete the "Media atleta" one. The single
            // image/video above stays as the primary media.
            canUploadTrainer={canEdit}
            canUploadAthlete={isTargetAthlete}
            onPatch={(patch) =>
              setMediaEdit((prev) =>
                prev
                  ? { ...prev, stagedSet: { ...prev.stagedSet, ...patch } }
                  : prev,
              )
            }
          />
        </Modal>
      )}
    </div>
  );
}

type Props = {
  exercise: ExerciseEntry;
  exIndex: number;
  splitIndex: number;
  canEdit: boolean;
  isTargetAthlete: boolean;
  entityOpts: EntityOptionsState;
  // PROG-WEEK-PARAMS/FE-4 — bundle for the trainer-view ExerciseProgressionEditor.
  // Optional: when absent the editor still renders, falling back to free-text
  // inputs for cascading-catalog fields (FE-1 fallback contract).
  progEditorOptions?: ProgEditorOptions;
  // PROG-DETAIL-NEW/3 — mutations bundle threaded into ProgrammazioneDetailEditor
  // for the pencil → InlineEditModal → updateProgressionWeek/Set commit flow.
  // Optional so legacy/test consumers that only render the read-only chrome
  // keep working without wiring the bundle.
  mutations?: ReturnType<typeof useProgrammazioneMutations>;
  // PROG-DETAIL identity flow — catalog + muscle-group options for the
  // muscle-group Tag resolution and the edit-identity modal.
  exercisesOptions: Option[];
  muscleGroupOptions: Option[];
  onDeleteExercise: () => void;
  onPasteExercise?: () => void;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
};

export default function ExerciseCardView({
  exercise,
  exIndex,
  splitIndex,
  canEdit,
  isTargetAthlete,
  progEditorOptions,
  mutations,
  exercisesOptions,
  muscleGroupOptions,
  onDeleteExercise,
  onPasteExercise,
  dragHandleProps,
}: Props) {
  const { copyExercise } = useClipboard();
  const [editOpen, setEditOpen] = useState(false);

  // "Aggiungi settimana/e" — opens the full progression editor (same component
  // as the create/edit programmazione, which mirrors the precompiled form) in a
  // modal, staged on a clone of this exercise's progression so changes only
  // commit on Save.
  const [weeksOpen, setWeeksOpen] = useState(false);
  const [stagedProgression, setStagedProgression] =
    useState<ExerciseProgression | null>(null);
  const [savingWeeks, setSavingWeeks] = useState(false);

  const openWeeksEditor = useCallback(() => {
    const current = exercise.progression;
    setStagedProgression(
      current
        ? (JSON.parse(JSON.stringify(current)) as ExerciseProgression)
        : { weeks: [], source: { kind: "inline" } },
    );
    setWeeksOpen(true);
  }, [exercise.progression]);

  const commitWeeks = useCallback(() => {
    if (!mutations || !stagedProgression) return;
    setSavingWeeks(true);
    Promise.resolve(
      mutations.updateExerciseProgression(splitIndex, exIndex, stagedProgression),
    ).finally(() => {
      setSavingWeeks(false);
      setWeeksOpen(false);
    });
  }, [mutations, stagedProgression, splitIndex, exIndex]);

  // "Associa progressione" — pick a precompiled progression from the library
  // and attach it to this exercise (same picker as the create/edit form).
  const [pickerOpen, setPickerOpen] = useState(false);
  // PROG-ATTACH-MERGE — when attaching onto an exercise that already has weeks,
  // stash the picked result and ask replace vs append (mirrors the edit form).
  const [pendingAttach, setPendingAttach] =
    useState<PrecompiledProgressionPickerResult | null>(null);
  const handlePickerConfirm = useCallback(
    (result: PrecompiledProgressionPickerResult) => {
      setPickerOpen(false);
      if (shouldPromptAttachMode(exercise.progression)) {
        setPendingAttach(result);
        return;
      }
      if (mutations) {
        mutations.updateExerciseProgression(
          splitIndex,
          exIndex,
          mergeAttachedProgression(exercise.progression, result, "replace"),
        );
      }
    },
    [mutations, splitIndex, exIndex, exercise.progression],
  );

  const resolvePendingAttach = useCallback(
    (mode: "replace" | "append") => {
      if (pendingAttach && mutations) {
        mutations.updateExerciseProgression(
          splitIndex,
          exIndex,
          mergeAttachedProgression(exercise.progression, pendingAttach, mode),
        );
      }
      setPendingAttach(null);
    },
    [mutations, splitIndex, exIndex, exercise.progression, pendingAttach],
  );

  // Collega/Scollega + "aggiornamento disponibile" (view side). Toggling the
  // link is persisted through the same inline-mutation path as everything else.
  const progression = exercise.progression;
  const libraryProgressionId =
    progression?.source.kind === "library"
      ? progression.source.precompiledProgressionId
      : null;
  const libraryMeta = usePrecompiledProgressionMeta(libraryProgressionId);
  const isLinkedLibrary =
    progression?.source.kind === "library" && !progression.source.detached;
  const isDetachedLibrary =
    progression?.source.kind === "library" && progression.source.detached === true;
  const updateAvailable = hasLibraryUpdate(progression, libraryMeta?.updatedAt);

  const setLinked = useCallback(
    (detached: boolean) => {
      if (!mutations || !progression || progression.source.kind !== "library") return;
      mutations.updateExerciseProgression(splitIndex, exIndex, {
        ...progression,
        source: { ...progression.source, detached },
      });
    },
    [mutations, progression, splitIndex, exIndex],
  );

  const handleApplyLibraryUpdate = useCallback(() => {
    if (!mutations || !progression || progression.source.kind !== "library") return;
    if (!libraryMeta?.weeks) return;
    mutations.updateExerciseProgression(
      splitIndex,
      exIndex,
      applyLibraryUpdate(progression, libraryMeta.weeks, libraryMeta.updatedAt),
    );
  }, [mutations, progression, libraryMeta, splitIndex, exIndex]);

  const muscleGroupName = useMemo(
    () =>
      exercise.muscleGroupId != null
        ? muscleGroupOptions.find((o) => o.value === exercise.muscleGroupId)?.label
        : undefined,
    [muscleGroupOptions, exercise.muscleGroupId],
  );

  // PROG-DETAIL identity flow — persist the edited identity field-by-field
  // through updateExerciseField. For catalog we set exerciseId + customName
  // (the catalog name) + muscleGroupId and leave nothing dangling; for custom
  // we clear exerciseId and write the free-text customName.
  const handleEditConfirm = useCallback(
    (identity: ExerciseIdentity) => {
      if (!mutations) return;
      // Apply every identity field in ONE batched mutation. Chaining separate
      // updateExerciseField calls fires concurrent save PUTs that each start
      // from the same stale programmazione, so the last one to land wins and
      // silently reverts the others (e.g. the new exerciseId) — that was the
      // "first GET keeps the previous exercise" bug.
      if (identity.type === "catalog") {
        const catalogEx = exercisesOptions.find(
          (o) => o.value?.id === identity.exerciseId,
        )?.value;
        mutations.updateExerciseFields(splitIndex, exIndex, {
          type: "catalog",
          muscleGroupId: identity.muscleGroupId,
          exerciseId: identity.exerciseId,
          customName: catalogEx?.name,
          // Adopt the chosen catalog exercise's image. `imageUrl` here is the
          // S3 key, which the BE signs on the save response / next GET (same
          // bucket as programmazione media). When the new exercise has no
          // image we clear it, and we always drop any pending local `image`
          // file so the picture never mismatches the chosen exercise.
          imageUrl: catalogEx?.imageUrl ?? undefined,
          image: undefined,
        });
      } else {
        mutations.updateExerciseFields(splitIndex, exIndex, {
          type: "custom",
          muscleGroupId: identity.muscleGroupId,
          exerciseId: undefined,
          customName: identity.customName,
        });
      }
      setEditOpen(false);
    },
    [mutations, splitIndex, exIndex, exercisesOptions],
  );
  // Name of the source library progression (resolved from its id) so the
  // detail view references which precompiled progression this exercise uses.
  const libraryProgressionName = usePrecompiledProgressionName(
    exercise.progression?.source.kind === "library"
      ? exercise.progression.source.precompiledProgressionId
      : null,
  );
  const screens = Grid.useBreakpoint();
  const isCompact = !screens.sm;
  // PROG-WEEK-PARAMS/FE-4 — `hasDetails` now includes a progression so the
  // expand chevron surfaces on cards that only carry programming, and the
  // body actually renders the read-only ExerciseProgressionEditor.
  const hasProgression = Boolean(
    exercise.progression && (exercise.progression.weeks?.length ?? 0) > 0,
  );
  // PROG-SET-MEDIA/FE-VIEW-1 — surface "has-details" also when any set
  // inside the exercise's progression carries a note, imageUrl, or
  // videoUrl, so the user lands with the per-set Allegati visible
  // (matching the K1 default-expand pattern from PROG-DETAIL/IMPL).
  const hasSetMedia = useMemo(() => {
    const weeks = (exercise.progression as { weeks?: PrecompiledWeek[] } | undefined)
      ?.weeks;
    if (!Array.isArray(weeks)) return false;
    return weeks.some((w) =>
      (w.sets ?? []).some(
        (s: PrecompiledSet) => s.note || s.imageUrl || s.videoUrl,
      ),
    );
  }, [exercise.progression]);
  const hasDetails = Boolean(
    exercise.imageUrl ||
      exercise.videoUrl ||
      exercise.notes ||
      hasProgression ||
      hasSetMedia ||
      // A trainer can always expand the card — even an exercise with no weeks
      // yet — so the "Aggiungi settimana/e" action is reachable.
      canEdit,
  );
  // Exercises start COLLAPSED by default (user preference): an expanded split
  // shows a compact list of exercise names; the trainer opens the specific
  // exercise to see its media / progression.
  const [expanded, setExpanded] = useState(false);

  const exerciseName =
    exercise.type === "catalog"
      ? exercise.customName || "Esercizio da catalogo"
      : exercise.customName || "Esercizio personalizzato";

  return (
    <Card className="exercise-detail-card mb-3">
      <Flex vertical gap="middle">
        <Flex
          align="center"
          gap="small"
          style={{ cursor: hasDetails ? "pointer" : "default" }}
          onClick={hasDetails ? () => setExpanded((prev) => !prev) : undefined}
        >
          {dragHandleProps && (
            <span
              {...dragHandleProps}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "grab", color: "var(--app-gray-400)", fontSize: 12, padding: "0 2px" }}
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
          )}
          {hasDetails && (
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              style={{ color: "var(--app-gray-500)", fontSize: 12 }}
            />
          )}
          <div className="exercise-number-badge">{exIndex + 1}</div>
          {/* Always-visible image thumbnail: every exercise card shows the
              preview without expanding, and clicking it opens the antD
              fullscreen preview (stopPropagation so it doesn't toggle the
              card). */}
          {exercise.imageUrl && (
            <span
              onClick={(e) => e.stopPropagation()}
              style={{ display: "inline-flex", flexShrink: 0 }}
            >
              {isVideoSrc(exercise.imageUrl) ? (
                // Catalog/exercise media can be a video — animate it inline.
                <VideoThumb
                  src={exercise.imageUrl}
                  title={exerciseName}
                  onClick={() =>
                    window.open(
                      exercise.imageUrl!,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  style={{
                    width: 44,
                    height: 44,
                    border: "1px solid var(--app-gray-200)",
                  }}
                />
              ) : (
                <Image
                  src={exercise.imageUrl}
                  alt={exerciseName}
                  width={44}
                  height={44}
                  style={{
                    objectFit: "cover",
                    borderRadius: 8,
                    border: "1px solid var(--app-gray-200)",
                  }}
                  fallback={IMAGE_FALLBACK}
                  preview={{
                    mask: <FontAwesomeIcon icon={faMagnifyingGlassPlus} />,
                  }}
                />
              )}
            </span>
          )}
          {/* Video-only exercise: show a moving thumbnail at the header too, so
              the clip animates in the anteprima and not only when expanded. */}
          {!exercise.imageUrl && exercise.videoUrl && (
            <span
              onClick={(e) => e.stopPropagation()}
              style={{ display: "inline-flex", flexShrink: 0 }}
            >
              <VideoThumb
                src={exercise.videoUrl}
                title={exerciseName}
                onClick={() =>
                  window.open(
                    exercise.videoUrl!,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                style={{
                  width: 44,
                  height: 44,
                  border: "1px solid var(--app-gray-200)",
                }}
              />
            </span>
          )}
          <Flex vertical style={{ flex: 1 }}>
            <strong className="exercise-name">{exerciseName}</strong>
            <Flex gap="small" wrap="wrap" className="mt-1">
              {/* PROG-DETAIL/IMPL K5 — hide the type tag on <sm to keep the
                  card compact; the name already conveys identity, while
                  SuperSet stays visible (it's load-bearing data). */}
              {!isCompact && (
                <span
                  className={`cm-tag ${
                    exercise.type === "catalog" ? "cm-tag--info" : "cm-tag--accent"
                  }`}
                >
                  {exercise.type === "catalog" ? "Catalogo" : "Personalizzato"}
                </span>
              )}
              {muscleGroupName && (
                <span className="cm-tag cm-tag--neutral">{muscleGroupName}</span>
              )}
              {exercise.isSuperSet && (
                <span className="cm-tag cm-tag--super">SuperSet</span>
              )}
            </Flex>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>
            <Flex align="center" gap={4}>
              {canEdit && (
                <Button
                  type="text"
                  size="small"
                  icon={<FontAwesomeIcon icon={faPen} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditOpen(true);
                  }}
                  title="Modifica esercizio"
                  aria-label="Modifica esercizio"
                />
              )}
              <ActionButtons
                onCopy={() => copyExercise(exercise)}
                onPaste={onPasteExercise}
                onDelete={onDeleteExercise}
                copyTooltip="Copia Esercizio"
                pasteTooltip="Incolla Esercizio"
                deleteTooltip="Elimina Esercizio"
                canEdit={canEdit}
                size="small"
              />
            </Flex>
          </div>
        </Flex>

        <div style={{ display: hasDetails && expanded ? "block" : "none" }}>
          <Flex vertical gap="middle">
            {exercise.imageUrl && (
              <div style={{ textAlign: "center" }}>
                {isVideoSrc(exercise.imageUrl) ? (
                  <video
                    src={exercise.imageUrl}
                    muted
                    loop
                    autoPlay
                    playsInline
                    controls
                    style={{
                      maxHeight: 220,
                      width: "100%",
                      maxWidth: 400,
                      borderRadius: 8,
                      backgroundColor: "#000",
                    }}
                  />
                ) : (
                  <Image
                    src={exercise.imageUrl}
                    alt={exerciseName}
                    style={{
                      maxHeight: 220,
                      objectFit: "contain",
                      borderRadius: 8,
                    }}
                    fallback={IMAGE_FALLBACK}
                  />
                )}
              </div>
            )}

            {exercise.videoUrl && (
              <div style={{ textAlign: "center" }}>
                {/* PROG-DETAIL/IMPL K3 — inline video preview (mirrors image
                    treatment) so the form-execution clip is visible at a
                    glance. The "open in new tab" link stays as a secondary
                    affordance for fullscreen. */}
                <video
                  controls
                  src={exercise.videoUrl}
                  style={{
                    maxHeight: 220,
                    width: "100%",
                    maxWidth: 400,
                    borderRadius: 8,
                    backgroundColor: "#000",
                  }}
                />
                <div className="mt-1">
                  <a
                    href={exercise.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="cm-tag cm-tag--accent cm-tag--clickable">
                      <FontAwesomeIcon icon={faVideo} />
                      Apri a schermo intero
                    </span>
                  </a>
                </div>
              </div>
            )}

            {exercise.notes && (
              <Alert message={exercise.notes} type="info" showIcon className="mb-0" />
            )}

            {/* PROG-SET-MEDIA/FE-VIEW-1 — per-set notes + media surfaced as
                a read-only summary block. Hidden entirely when no set
                inside the exercise's progression has any of
                note/imageUrl/videoUrl. Rendered ABOVE the full progression
                editor so the trainer/athlete sees the per-set Allegati
                callouts before scrolling through the matrix. */}
            <SetMediaSummary
              exercise={exercise}
              splitIndex={splitIndex}
              exIndex={exIndex}
              canEdit={canEdit}
              isTargetAthlete={isTargetAthlete}
              mutations={mutations}
            />

            {/* PROG-DETAIL-NEW/1 (#259a03b2) — read-only Detail chrome (ppd-*
                week cards + per-set rows), ported from the library Detail
                page. Replaces the previous trainer-view ExerciseProgressionEditor
                mount; the editor stays in place for EditProgrammazione. */}
            {hasProgression && libraryProgressionName && (
              <div
                className="view-progression-ref"
                data-testid={`view-progression-name-${exIndex}`}
              >
                <span className="cm-tag cm-tag--brand">Da libreria</span>
                <span className="view-progression-ref-name">
                  {libraryProgressionName}
                </span>
              </div>
            )}
            {hasProgression && (
              <div data-testid={`view-progression-${exIndex}`}>
                <ProgrammazioneDetailEditor
                  value={exercise.progression!}
                  options={progEditorOptions}
                  splitIndex={splitIndex}
                  exerciseIndex={exIndex}
                  isTrainer={canEdit}
                  isAthlete={isTargetAthlete}
                  mutations={mutations}
                  displayMode="detail"
                />
              </div>
            )}

            {canEdit && (
              <Flex gap={8} wrap="wrap">
                <Button
                  type="default"
                  className="cm-btn cm-btn--ghost"
                  icon={<FontAwesomeIcon icon={faCalendarPlus} />}
                  onClick={openWeeksEditor}
                  data-testid={`view-add-weeks-${exIndex}`}
                >
                  {hasProgression ? "Aggiungi / modifica settimane" : "Aggiungi settimana/e"}
                </Button>
                <Button
                  type="default"
                  className="cm-btn cm-btn--ghost"
                  icon={<FontAwesomeIcon icon={faFileImport} />}
                  onClick={() => setPickerOpen(true)}
                  data-testid={`view-associate-progression-${exIndex}`}
                >
                  Associa progressione
                </Button>
                {updateAvailable && (
                  <Button
                    type="default"
                    className="cm-btn cm-btn--ghost"
                    icon={<FontAwesomeIcon icon={faRotate} />}
                    onClick={handleApplyLibraryUpdate}
                    data-testid={`view-progression-apply-update-${exIndex}`}
                  >
                    Aggiorna dalla libreria
                  </Button>
                )}
                {isLinkedLibrary && (
                  <Button
                    type="default"
                    className="cm-btn cm-btn--ghost"
                    icon={<FontAwesomeIcon icon={faLinkSlash} />}
                    onClick={() => setLinked(true)}
                    data-testid={`view-progression-detach-${exIndex}`}
                  >
                    Scollega
                  </Button>
                )}
                {isDetachedLibrary && (
                  <Button
                    type="default"
                    className="cm-btn cm-btn--ghost"
                    icon={<FontAwesomeIcon icon={faLink} />}
                    onClick={() => setLinked(false)}
                    data-testid={`view-progression-relink-${exIndex}`}
                  >
                    Collega
                  </Button>
                )}
              </Flex>
            )}
            {(isLinkedLibrary || isDetachedLibrary) && (
              <Flex gap={6} align="center" wrap="wrap" style={{ marginTop: 4 }}>
                <span
                  className={`cm-tag ${isDetachedLibrary ? "cm-tag--neutral" : "cm-tag--brand"}`}
                  data-testid={`view-progression-link-state-${exIndex}`}
                >
                  <FontAwesomeIcon icon={isDetachedLibrary ? faLinkSlash : faLink} />
                  {isDetachedLibrary ? "Scollegata dalla libreria" : "Collegata alla libreria"}
                </span>
                {updateAvailable && (
                  <span className="cm-tag cm-tag--super">
                    <FontAwesomeIcon icon={faRotate} />
                    Aggiornamento disponibile
                  </span>
                )}
              </Flex>
            )}
          </Flex>
        </div>
      </Flex>

      {canEdit && pickerOpen && (
        <Suspense fallback={null}>
          <PrecompiledProgressionPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onConfirm={handlePickerConfirm}
          />
        </Suspense>
      )}

      <Modal
        open={!!pendingAttach}
        onCancel={() => setPendingAttach(null)}
        title="Collega progressione"
        data-testid={`view-progression-attach-mode-${exIndex}`}
        footer={[
          <Button
            key="append"
            onClick={() => resolvePendingAttach("append")}
            data-testid={`view-progression-attach-append-${exIndex}`}
          >
            Aggiungi in coda
          </Button>,
          <Button
            key="replace"
            type="primary"
            danger
            onClick={() => resolvePendingAttach("replace")}
            data-testid={`view-progression-attach-replace-${exIndex}`}
          >
            Sostituisci
          </Button>,
        ]}
      >
        Questo esercizio ha già {exercise.progression?.weeks?.length ?? 0}{" "}
        settimane configurate.
        <br />
        <strong>Sostituisci</strong> le sovrascrive con la progressione scelta;{" "}
        <strong>Aggiungi in coda</strong> le mantiene e accoda le nuove settimane
        a seguire (la progressione diventa personalizzata).
      </Modal>

      {canEdit && weeksOpen && (
        <Modal
          open
          title="Settimane di progressione"
          okText="Salva"
          cancelText="Annulla"
          confirmLoading={savingWeeks}
          onOk={commitWeeks}
          onCancel={() => setWeeksOpen(false)}
          width={screens.md ? 1000 : "100%"}
          style={screens.md ? { top: 24 } : { top: 0, paddingBottom: 0, maxWidth: "100%" }}
          styles={{ body: { maxHeight: "78vh", overflowY: "auto" } }}
          destroyOnClose
        >
          {stagedProgression && (
            <Suspense fallback={null}>
              <ExerciseProgressionEditor
                value={stagedProgression}
                onChange={setStagedProgression}
                mode="trainer-edit"
                options={progEditorOptions}
              />
            </Suspense>
          )}
        </Modal>
      )}

      {canEdit && (
        <ExerciseIdentityModal
          open={editOpen}
          mode="edit"
          initial={{
            type: exercise.type,
            muscleGroupId: exercise.muscleGroupId,
            exerciseId: exercise.exerciseId,
            customName: exercise.customName,
          }}
          exercisesOptions={exercisesOptions}
          muscleGroupOptions={muscleGroupOptions}
          onCancel={() => setEditOpen(false)}
          onConfirm={handleEditConfirm}
        />
      )}
    </Card>
  );
}
